// src/pages/AutoEditPage.jsx - Dedicated edit page for Auto with "Indietro" back button
import { Button } from "@/components/ui/button";
import { supabase } from '@/lib/supabase';
import { autoService } from '@/services/SupabaseService';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AutoForm from "../components/auto/AutoForm";
import { useAuth } from "../contexts/AuthContext";
import ActivityService from "../services/ActivityService";

export default function AutoEditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Preserve filter query params for back-navigation
    const returnUrl = `/auto${location.search}`;
    const queryClient = useQueryClient();
    const { profile } = useAuth();
    const isViewer = profile?.role === 'viewer';

    const [lastError, setLastError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen size
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Redirect viewers away
    useEffect(() => {
        if (isViewer) {
            navigate('/auto');
        }
    }, [isViewer, navigate]);

    // Load auto record
    const { data: autoItem, isLoading: autoLoading } = useQuery({
        queryKey: ['auto', id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('auto')
                .select('*')
                .eq('id', id)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: !!id
    });

    // Load reference data
    const { data: clienti = [], isLoading: clientiLoading } = useQuery({
        queryKey: ['clienti'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('clienti')
                .select('*')
                .order('nome');
            if (error) throw error;
            return data || [];
        }
    });

    const { data: stati = [], isLoading: statiLoading } = useQuery({
        queryKey: ['stati'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('stati')
                .select('*')
                .order('nome');
            if (error) throw error;
            return data || [];
        }
    });

    const { data: tettoie = [], isLoading: tettoieLoading } = useQuery({
        queryKey: ['tettoie'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('tettoie')
                .select('*')
                .order('nome');
            if (error) throw error;
            return data || [];
        }
    });

    const { data: sezioni = [], isLoading: sezioniLoading } = useQuery({
        queryKey: ['sezioni'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('sezioni')
                .select('*')
                .order('nome');
            if (error) throw error;
            return data || [];
        }
    });

    const { data: campate = [], isLoading: campateLoading } = useQuery({
        queryKey: ['campate'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('campate')
                .select('*')
                .order('nome');
            if (error) throw error;
            return data || [];
        }
    });

    const isLoading = autoLoading || clientiLoading || statiLoading ||
        tettoieLoading || sezioniLoading || campateLoading;

    // Helper functions for activity logging
    const getClienteName = (clienteId) => {
        const cliente = clienti.find(c => c.id === clienteId);
        return cliente ? `${cliente.nome} ${cliente.cognome}` : "Cliente sconosciuto";
    };

    const getStatoName = (statoId) => {
        const stato = stati.find(s => s.id === statoId);
        return stato ? stato.nome : "Stato sconosciuto";
    };

    const getCampataFullName = (campataId) => {
        if (!campataId) return "N/D";
        const campata = campate.find(c => c.id === campataId);
        if (!campata) return "N/D";
        const sezione = sezioni.find(s => s.id === campata.sezione_id);
        if (!sezione) return campata.nome;
        const tettoia = tettoie.find(t => t.id === sezione.tettoia_id);
        if (!tettoia) return `${sezione.nome} / ${campata.nome}`;
        return `${tettoia.nome} / ${sezione.nome} / ${campata.nome}`;
    };

    // Check numero_telaio duplicates
    const handleCheckNumeroTelaio = async (numeroTelaio, excludeAutoId = null) => {
        try {
            return await autoService.checkNumeroTelaioDuplicate(numeroTelaio, excludeAutoId);
        } catch (error) {
            console.error('Error checking numero_telaio duplicate:', error);
            return { isDuplicate: false, existingAuto: null };
        }
    };

    // Helper function to get detailed error message
    const getDetailedErrorMessage = (error, operation = "operazione") => {
        console.error(`Error in ${operation}:`, error);
        if (error?.code) {
            switch (error.code) {
                case '23505':
                    return `Errore: Valore duplicato. ${error.details || 'Un record con questi dati esiste già.'}`;
                case '23503':
                    return `Errore: Riferimento non valido. ${error.details || 'Uno dei valori selezionati non è più disponibile.'}`;
                case '23502':
                    return `Errore: Campo obbligatorio mancante. ${error.details || 'Tutti i campi richiesti devono essere compilati.'}`;
                case '42501':
                    return `Errore: Permessi insufficienti. ${error.details || 'Non hai i permessi per questa operazione.'}`;
                default:
                    return `Errore database (${error.code}): ${error.message || error.details || 'Errore sconosciuto'}`;
            }
        }
        if (error?.message) {
            return `Errore durante ${operation}: ${error.message}`;
        }
        return `Errore sconosciuto durante ${operation}. Riprova o contatta l'assistenza.`;
    };

    // Update mutation
    const updateAutoMutation = useMutation({
        mutationFn: async ({ id: autoId, data: updateData, oldData }) => {
            console.log('🔄 Updating auto:', { autoId, updateData, oldData });
            setLastError(null);

            const { data: result, error } = await supabase
                .from('auto')
                .update(updateData)
                .eq('id', autoId)
                .select()
                .single();

            if (error) {
                console.error('❌ Error updating auto:', error);
                throw error;
            }

            console.log('✅ Auto updated successfully:', result);

            // Enhanced field mappings for better change descriptions
            const fieldMappings = {
                targa: 'Targa',
                marca: 'Marca',
                modello: 'Modello',
                numero_telaio: 'Numero Telaio',
                cliente_id: `Cliente (${getClienteName(oldData.cliente_id)} → ${getClienteName(result.cliente_id)})`,
                stato_id: `Stato (${getStatoName(oldData.stato_id)} → ${getStatoName(result.stato_id)})`,
                campata_id: `Posizione (${getCampataFullName(oldData.campata_id)} → ${getCampataFullName(result.campata_id)})`,
                data_ingresso: 'Data Ingresso',
                data_uscita: 'Data Uscita',
                note: 'Note'
            };

            // Log the UPDATE activity
            const identifier = result.targa || oldData.targa || `Auto ID: ${autoId}`;
            try {
                await ActivityService.logUpdate('auto', autoId, oldData, result, identifier, fieldMappings);
            } catch (logError) {
                console.warn('⚠️ Failed to log activity, but auto was updated successfully:', logError);
            }

            return result;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['auto'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
            setLastError(null);
            navigate(returnUrl);
        },
        onError: (error) => {
            console.error('💥 Update mutation failed:', error);
            const errorMessage = getDetailedErrorMessage(error, "aggiornamento auto");
            setLastError(errorMessage);
        }
    });

    // Submit handler
    const handleSubmit = async (autoData) => {
        try {
            setLastError(null);
            await updateAutoMutation.mutateAsync({
                id: autoItem.id,
                data: autoData,
                oldData: autoItem
            });
        } catch (error) {
            console.error("💥 Error in handleSubmit:", error);
        }
    };

    const handleCancel = () => {
        navigate(returnUrl);
    };

    // Loading state
    if (isLoading) {
        return (
            <div className={`${isMobile ? 'p-3' : 'p-4 md:p-8'} min-h-screen`}>
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-slate-600">Caricamento dati auto...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Auto not found
    if (!autoItem && !autoLoading) {
        return (
            <div className={`${isMobile ? 'p-3' : 'p-4 md:p-8'} min-h-screen`}>
                <div className="max-w-4xl mx-auto">
                    <Button
                        onClick={handleCancel}
                        variant="ghost"
                        className="mb-6 text-slate-600 hover:text-slate-800 hover:bg-slate-100 -ml-2"
                        style={{ minHeight: '44px' }}
                    >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Indietro
                    </Button>
                    <div className="text-center py-12">
                        <h2 className="text-xl font-semibold text-slate-700 mb-2">Auto non trovata</h2>
                        <p className="text-slate-500">L'auto richiesta non esiste o è stata rimossa.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${isMobile ? 'p-3' : 'p-4 md:p-8'} min-h-screen`}>
            <div className="max-w-4xl mx-auto">
                {/* Back button */}
                <Button
                    onClick={handleCancel}
                    variant="ghost"
                    className="mb-4 text-slate-600 hover:text-slate-800 hover:bg-slate-100 -ml-2"
                    style={{ minHeight: '44px', touchAction: 'manipulation' }}
                >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Indietro
                </Button>

                {/* Error display */}
                {lastError && (
                    <div className={`mb-4 p-4 bg-red-50 border border-red-200 rounded-lg`}>
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-sm font-medium text-red-800">Errore nell'operazione</h3>
                                <p className="mt-1 text-sm text-red-700">{lastError}</p>
                                <div className="mt-2">
                                    <Button
                                        onClick={() => setLastError(null)}
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 border-red-300 hover:bg-red-50"
                                    >
                                        Chiudi
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Auto Form */}
                <AutoForm
                    auto={autoItem}
                    clienti={clienti}
                    tettoie={tettoie}
                    sezioni={sezioni}
                    campate={campate}
                    stati={stati}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    isLoading={updateAutoMutation.isPending}
                    onCheckNumeroTelaio={handleCheckNumeroTelaio}
                    isMobile={isMobile}
                />

                {/* Bottom spacing for mobile */}
                <div className={isMobile ? 'h-16' : 'h-8'}></div>
            </div>
        </div>
    );
}
